import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import http from '../../services/http';
import { LOCALE_STORAGE_KEY, setLocale as persistLocale } from '../../i18n';

interface LocaleRow {
    code: string;
    name: string;
    is_active: boolean;
}

export default function LocaleSwitcher() {
    const { t } = useTranslation();
    const [locales, setLocales] = useState<LocaleRow[]>([]);
    const [current, setCurrent] = useState(() => localStorage.getItem(LOCALE_STORAGE_KEY) ?? 'en');
    const [isOpen, setIsOpen] = useState(false);
    const [isLoadingList, setIsLoadingList] = useState(true);
    const [isSwitching, setIsSwitching] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        let isMounted = true;

        http.get('/api/locales', { params: { active_only: true } })
            .then(({ data }) => {
                if (isMounted) setLocales(data.data);
            })
            .catch(() => {
                if (isMounted) setLocales([]);
            })
            .finally(() => {
                if (isMounted) setIsLoadingList(false);
            });

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        const onClickOutside = (event: MouseEvent) => {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('click', onClickOutside);
        return () => document.removeEventListener('click', onClickOutside);
    }, []);

    if (isLoadingList) {
        return (
            <div className="flex items-center justify-center px-2.5 py-1.5">
                <i className="fas fa-spinner fa-spin text-xs text-gray-400" aria-label="Loading languages" />
            </div>
        );
    }

    if (locales.length < 2) {
        return null;
    }

    const select = async (code: string) => {
        if (code === current || isSwitching) {
            setIsOpen(false);
            return;
        }

        try {
            setIsSwitching(true);
            setIsOpen(false);
            setCurrent(code);
            await persistLocale(code);
        } finally {
            setIsSwitching(false);
        }
    };

    return (
        <div ref={ref} className="relative">
            <button
                type="button"
                disabled={isSwitching}
                onClick={() => setIsOpen((open) => !open)}
                title={t('common.locale_switcher.language')}
                className="flex items-center gap-1 rounded-full bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-60"
            >
                {isSwitching ? (
                    <i className="fas fa-spinner fa-spin text-[11px]" aria-hidden="true" />
                ) : (
                    <i className="fas fa-globe text-[11px]" aria-hidden="true" />
                )}
                <span className="uppercase">{current}</span>
            </button>

            {isOpen && (
                <div className="absolute top-full right-0 z-60 mt-2 w-40 rounded-md border border-gray-200 bg-white py-1 shadow-lg">
                    {locales.map((locale) => (
                        <button
                            key={locale.code}
                            type="button"
                            onClick={() => void select(locale.code)}
                            className={`block w-full px-3 py-1.5 text-left text-xs hover:bg-gray-100 ${
                                locale.code === current ? 'font-semibold text-blue-600' : 'text-gray-700'
                            }`}
                        >
                            {locale.name}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
